import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { useNavigate } from 'react-router-dom';
import {
  Sparkle, ArrowRight, X, ArrowsClockwise, CheckCircle, Sun, Moon,
  ForkKnife, ShieldCheck, Cake, Confetti, Coffee, Baby, Barbell,
  Heart, ChatCircle, House, MagicWand,
} from '@phosphor-icons/react';

const ICONS = {
  Sun, Moon, ForkKnife, ShieldCheck, Cake, Confetti, Coffee, Baby, Barbell,
  Heart, ChatCircle, House, MagicWand,
};

/**
 * Magic Moments — anticipatory dashboard widget.
 * Shows ONE perfect contextual suggestion. One-tap accept executes the
 * action server-side (run scene / navigate / message). User can dismiss
 * or shuffle to "show another."
 */
export default function MagicMomentsWidget() {
  const { api } = useAuth();
  const { mode } = useTheme();
  const navigate = useNavigate();

  const [moment, setMoment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [executing, setExecuting] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [excludeIds, setExcludeIds] = useState([]);
  const [error, setError] = useState('');

  const fetchMoment = useCallback(async (excludes = []) => {
    setLoading(true);
    setError('');
    setAccepted(false);
    setDismissed(false);
    try {
      const params = excludes.length ? `?exclude=${excludes.join(',')}` : '';
      const res = await api.get(`/magic-moments/current${params}`);
      setMoment(res.data.moment);
    } catch (e) {
      setError('Could not load suggestion');
    }
    setLoading(false);
  }, [api]);

  useEffect(() => {
    fetchMoment();
    const t = setInterval(() => fetchMoment(), 15 * 60 * 1000); // refresh every 15 min
    return () => clearInterval(t);
  }, [fetchMoment]);

  const handleAccept = async () => {
    if (!moment) return;
    setExecuting(true);
    try {
      await api.post(`/magic-moments/${moment.moment_id}/accept`, {
        template_id: moment.template_id,
        action: moment.action,
      });
      setAccepted(true);
      // Client-side action follow-through
      const action = moment.action;
      if (action?.type === 'navigate' && action.path) {
        setTimeout(() => navigate(action.path), 800);
      }
      // For run_scene, the backend logs it + we show a confirmation here
      setTimeout(() => {
        setExcludeIds(prev => [...prev, moment.template_id]);
        fetchMoment([...excludeIds, moment.template_id]);
      }, 2400);
    } catch (e) {
      setError('Action failed — please try again');
    }
    setExecuting(false);
  };

  const handleDismiss = async () => {
    if (!moment) return;
    try {
      await api.post(`/magic-moments/${moment.moment_id}/dismiss`, {
        template_id: moment.template_id,
      });
    } catch (e) { /* ignore */ }
    setDismissed(true);
    setTimeout(() => {
      const newExclude = [...excludeIds, moment.template_id];
      setExcludeIds(newExclude);
      fetchMoment(newExclude);
    }, 400);
  };

  const handleShuffle = async () => {
    const newExclude = moment ? [...excludeIds, moment.template_id] : excludeIds;
    setExcludeIds(newExclude);
    fetchMoment(newExclude);
  };

  if (loading && !moment) {
    return (
      <div className="glass-card p-5 sm:p-6" data-testid="magic-moments-widget">
        <div className="h-32 flex items-center justify-center">
          <div className="w-6 h-6 border-2 border-purple-400 border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  if (!moment) {
    return null; // No suggestion right now → hide widget entirely
  }

  const Icon = ICONS[moment.icon] || MagicWand;
  const color = moment.color || '#A855F7';
  const isDark = !['light', 'kitchen'].includes(mode);

  return (
    <div className="glass-card p-5 sm:p-6 relative overflow-hidden"
      data-testid="magic-moments-widget"
      style={{
        background: `linear-gradient(135deg, ${color}10 0%, ${color}05 50%, transparent 100%)`,
        border: `1px solid ${color}30`,
      }}>

      {/* Decorative glow */}
      <div className="absolute -top-12 -right-12 w-40 h-40 rounded-full pointer-events-none"
        style={{ background: `${color}20`, filter: 'blur(40px)' }} />
      <div className="absolute -bottom-16 -left-16 w-44 h-44 rounded-full pointer-events-none"
        style={{ background: `${color}15`, filter: 'blur(60px)' }} />

      <div className="relative z-10">
        {/* Header label */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-1.5 px-2 py-0.5 rounded-full"
            style={{ background: `${color}25`, border: `1px solid ${color}40` }}>
            <Sparkle size={10} weight="fill" style={{ color }} />
            <span className="text-[9px] font-bold uppercase tracking-[0.18em]" style={{ color }}>
              Magic Moment
            </span>
          </div>
          <div className="flex items-center gap-1">
            <button onClick={handleShuffle}
              data-testid="magic-shuffle-btn"
              title="Show another suggestion"
              className="p-1.5 rounded-lg hover:bg-white/5 transition-colors"
              style={{ color: 'var(--hc-muted-text, #A3A3A3)' }}>
              <ArrowsClockwise size={13} weight="bold" />
            </button>
            <button onClick={handleDismiss}
              data-testid="magic-dismiss-btn"
              title="Dismiss"
              className="p-1.5 rounded-lg hover:bg-white/5 transition-colors"
              style={{ color: 'var(--hc-muted-text, #A3A3A3)' }}>
              <X size={14} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className={`flex gap-4 transition-all duration-400 ${dismissed ? 'opacity-0 -translate-x-4' : ''} ${accepted ? 'scale-[0.98]' : ''}`}>
          <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl flex items-center justify-center flex-shrink-0"
            style={{
              background: `linear-gradient(135deg, ${color}, ${color}AA)`,
              boxShadow: `0 8px 24px ${color}40`,
            }}>
            <Icon size={24} weight="duotone" className="text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-heading font-bold text-base sm:text-lg leading-tight mb-1.5"
              style={{ color: isDark ? '#FFFFFF' : 'var(--hc-page-text)' }}
              data-testid="magic-headline">
              {moment.headline}
            </h3>
            <p className="text-xs sm:text-sm leading-relaxed"
              style={{ color: 'var(--hc-muted-text, #A3A3A3)' }}
              data-testid="magic-subtitle"
              dangerouslySetInnerHTML={{ __html: formatMarkdown(moment.subtitle) }} />
          </div>
        </div>

        {/* Action button */}
        {!accepted && !dismissed && moment.action && (
          <div className="mt-4 sm:mt-5 flex flex-col sm:flex-row gap-2">
            <button onClick={handleAccept} disabled={executing}
              data-testid="magic-accept-btn"
              className="flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-full text-white text-sm font-semibold transition-all hover:scale-[1.01] active:scale-[0.99] disabled:opacity-50 disabled:cursor-not-allowed"
              style={{
                background: `linear-gradient(135deg, ${color}, ${color}DD)`,
                boxShadow: `0 6px 20px ${color}50`,
              }}>
              {executing ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  <span>{moment.action.label || 'Yes, do it'}</span>
                  <ArrowRight size={14} weight="bold" />
                </>
              )}
            </button>
            <button onClick={handleDismiss}
              data-testid="magic-not-now-btn"
              className="flex items-center justify-center gap-1 py-3 px-5 rounded-full text-xs font-medium hover:bg-white/5 transition-colors"
              style={{ color: 'var(--hc-muted-text, #A3A3A3)' }}>
              Not now
            </button>
          </div>
        )}

        {accepted && (
          <div className="mt-4 flex items-center gap-2 text-sm" data-testid="magic-accepted-banner">
            <CheckCircle size={16} weight="fill" style={{ color: '#22C55E' }} />
            <span style={{ color: '#22C55E' }} className="font-semibold">Done!</span>
            <span style={{ color: 'var(--hc-muted-text, #A3A3A3)' }} className="text-xs">
              Looking for the next moment...
            </span>
          </div>
        )}

        {error && (
          <p className="mt-3 text-xs" style={{ color: '#F87171' }}>{error}</p>
        )}
      </div>
    </div>
  );
}

// Tiny markdown renderer — just **bold**
function formatMarkdown(s) {
  if (!s) return '';
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/\*\*([^*]+)\*\*/g, '<strong style="color:var(--hc-page-text,#fff)">$1</strong>');
}
